/**
 * useMasterList — TanStack Query integration for the list view.
 *
 * Supports two modes:
 *   - 'pages'    : traditional page-based pagination via useQuery.
 *   - 'infinite' : infinite scroll via useInfiniteQuery; pages are accumulated
 *                  and flattened into the Zustand store on every page arrival.
 *
 * Both query hooks are always called (React hooks rules). Only the active one
 * is enabled via the `enabled` flag.
 *
 * @module hooks/useMasterList
 * @see FMPV-MVWC-E002-S004 I-001
 */
import { useEffect } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useMasterViewStore } from '../store/MasterViewContext';
import type { MasterPatternViewEntity } from '../types/service.types';
import type { MasterPatternViewService } from '../types/service.types';
import type { MasterPatternViewPermissions } from '../types/permissions.types';
import type { ToastCallback } from './useToast';
import { useToast } from './useToast';
import { isActionAllowed } from '../engine/permissionEngine';

export interface UseMasterListOptions<T extends MasterPatternViewEntity> {
  service: MasterPatternViewService<T>;
  entityName?: string;
  autoLoad?: boolean;
  defaultFiltersOpen?: boolean;
  permissions?: MasterPatternViewPermissions;
  masterType?: 'UNIVERSAL' | 'COMPANY_SPECIFIC' | 'GLOBAL';
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  mode?: 'pages' | 'infinite';
  /** Translation function — used for toast messages. Falls back to built-in Spanish strings. */
  t?: (key: string, defaultValue?: string) => string;
}

export function useMasterList<T extends MasterPatternViewEntity>({
  service,
  entityName = 'entity',
  autoLoad = false,
  defaultFiltersOpen = false,
  permissions,
  masterType = 'UNIVERSAL',
  toast,
  showInternalToasts = true,
  mode = 'pages',
  t = (key: string, defaultValue?: string) => defaultValue ?? key,
}: UseMasterListOptions<T>) {
  const searchParams = useMasterViewStore((s) => s.searchParams);
  const columnFilters = useMasterViewStore((s) => s.columnFilters);
  const quickFilterValues = useMasterViewStore((s) => s.quickFilterValues);
  const hasSearched = useMasterViewStore((s) => s.hasSearched);
  const setData = useMasterViewStore((s) => s.setData);
  const setIsLoading = useMasterViewStore((s) => s.setIsLoading);
  const setHasSearched = useMasterViewStore((s) => s.setHasSearched);
  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const { showError } = useToast(toast, showInternalToasts);

  const canRead = isActionAllowed(permissions, 'READ', masterType);
  const enabled = (hasSearched || autoLoad || !defaultFiltersOpen) && canRead;
  const isInfinite = mode === 'infinite';

  // Separate meta-filters (client-side only) from backend service filters.
  // __view      → applied after fetch to filter rows by isAssigned (not sent to backend)
  // __status    → mapped to isActive boolean for backend
  // __connector → connector hint for advanced filters (AND/OR), not a field filter
  // Same field with multiple conditions → value becomes an array of MasterPatternViewFilterValue
  // so backends can apply all conditions instead of only the last one.
  //
  // columnFilters (from ListTable header inputs) and quickFilterValues (from inline
  // QuickFilters inputs, converted to ActiveFilter[] with operator 'contains') are both
  // merged with searchParams.filters (from FilterPanel) here so all three sources remain
  // independent in the store — each mechanism can be cleared without affecting the others.
  const quickFiltersAsActive: import('../store/types').ActiveFilter[] = Object.entries(quickFilterValues)
    .filter(([, v]) => v.trim() !== '')
    .map(([field, v]) => ({ field, operator: 'contains', value: v.trim() }));
  const allFilters = [...searchParams.filters, ...columnFilters, ...quickFiltersAsActive];
  const serviceFilters: Record<string, unknown> = {};
  let viewFilter: string | undefined;

  for (const f of allFilters) {
    if (f.field === '__view') {
      viewFilter = String(f.value);
    } else if (f.field === '__status') {
      if (f.value === 'active') serviceFilters['isActive'] = true;
      else if (f.value === 'inactive') serviceFilters['isActive'] = false;
    } else if (f.field === '__connector') {
      // pass connector hint as-is so backends can use AND/OR logic
      serviceFilters['__connector'] = f.value;
    } else {
      const filterValue = { value: f.value, operator: f.operator };
      const existing = serviceFilters[f.field];
      if (existing === undefined) {
        serviceFilters[f.field] = filterValue;
      } else if (Array.isArray(existing)) {
        (existing as unknown[]).push(filterValue);
      } else {
        serviceFilters[f.field] = [existing, filterValue];
      }
    }
  }

  const companyId = masterType !== 'UNIVERSAL' ? activeCompanyContext : undefined;

  // Base params shared by both modes (page number is handled separately per mode).
  const baseParams = {
    pageSize: searchParams.pageSize,
    search: searchParams.search,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    filters: serviceFilters,
    companyId,
  };

  // ── Page-based query ────────────────────────────────────────────────────────
  // Enabled only when mode === 'pages'.
  const pageQuery = useQuery({
    queryKey: ['masterList', entityName, searchParams, columnFilters, quickFilterValues, companyId],
    queryFn: () => service.getAll({ page: searchParams.page, ...baseParams }),
    enabled: enabled && !isInfinite,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // ── Infinite query ──────────────────────────────────────────────────────────
  // Enabled only when mode === 'infinite'.
  // queryKey excludes page (managed by pageParam) so changing search/filters
  // resets accumulation automatically.
  const infiniteQuery = useInfiniteQuery({
    // pageParam drives pagination — no need to include searchParams.page in the key.
    // Any change to search/filters/sort lands in baseParams and invalidates this query automatically.
    queryKey: ['masterList', entityName, baseParams, companyId],
    queryFn: ({ pageParam }) =>
      service.getAll({ page: pageParam as number, ...baseParams }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.total / lastPage.pageSize);
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined;
    },
    enabled: enabled && isInfinite,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // ── Loading state sync ──────────────────────────────────────────────────────
  useEffect(() => {
    const active = isInfinite ? infiniteQuery : pageQuery;
    setIsLoading(active.isLoading || active.isFetching);
  }, [
    isInfinite,
    pageQuery.isLoading,
    pageQuery.isFetching,
    infiniteQuery.isLoading,
    infiniteQuery.isFetching,
    setIsLoading,
  ]);

  // ── Page-based data sync ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isInfinite && pageQuery.data) {
      let rows = pageQuery.data.data;
      let total = pageQuery.data.total;
      // `__view=company` filters client-side, over whatever page is already loaded —
      // it never re-fetches, so `pageQuery.data.total` (the server's UNFILTERED count)
      // no longer describes what's on screen once that filter drops rows. Left as-is,
      // the pagination bar kept advertising the full unfiltered total/page count (e.g.
      // "209 registros" / page 2 of 2) while rendering a single filtered row — user-visible
      // as "the pill doesn't find records that happen to live on a page I'm not viewing".
      // Reporting the post-filter row count instead stops the bar from lying about a
      // total or a further page that isn't actually there to page into; it does NOT make
      // the filter reach records sitting on pages this fetch never loaded — that needs the
      // filter to travel to the backend instead, a separate (larger) change.
      if (viewFilter === 'company') {
        rows = rows.filter((r) => r.isAssigned === true);
        total = rows.length;
      }
      setData(rows, total);
      setHasSearched(true);
    }
  }, [isInfinite, pageQuery.data, viewFilter, setData, setHasSearched]);

  // ── Infinite data sync ──────────────────────────────────────────────────────
  // Flatten all accumulated pages into a single array and push to store.
  useEffect(() => {
    if (isInfinite && infiniteQuery.data) {
      let rows = infiniteQuery.data.pages.flatMap((p) => p.data);
      let total = infiniteQuery.data.pages[0]?.total ?? 0;
      // Same rationale as the page-based sync above: report what's actually rendered
      // once `__view=company` drops rows, instead of the server's unfiltered total.
      if (viewFilter === 'company') {
        rows = rows.filter((r) => r.isAssigned === true);
        total = rows.length;
      }
      setData(rows, total);
      setHasSearched(true);
    }
  }, [isInfinite, infiniteQuery.data, viewFilter, setData, setHasSearched]);

  // ── Error handling ──────────────────────────────────────────────────────────
  useEffect(() => {
    const err = isInfinite ? infiniteQuery.error : pageQuery.error;
    if (err) showError(t('loadRecordsError', 'Error al cargar los registros'));
  }, [isInfinite, pageQuery.error, infiniteQuery.error, showError, t]);

  return {
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage ?? false,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
}
